import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, Award, Shield, RefreshCw, Users } from 'lucide-react';
import StarRating from '../components/shared/StarRating';

const HomePage = () => {
  const guides = [
    {
      title: 'LLC in Wyoming gründen — Der ultimative Leitfaden 2026',
      excerpt: 'Warum Wyoming der beste Bundesstaat für Ihre LLC ist: Keine Staatseinkommensteuer, starker Datenschutz und niedrige Gebühren.',
      href: '/ratgeber/llc-wyoming-gruenden',
      category: 'LLC USA'
    },
    {
      title: 'EIN Nummer beantragen — Schritt-für-Schritt',
      excerpt: 'Alles was Sie über die Employer Identification Number wissen müssen und wie Sie diese als Nicht-US-Bürger beantragen.',
      href: '/ratgeber/ein-nummer-beantragen',
      category: 'Steuern'
    },
    {
      title: 'US-Bankkonto eröffnen als Ausländer',
      excerpt: 'Die besten Banken für Nicht-Residenten und wie Sie erfolgreich ein Geschäftskonto für Ihre LLC eröffnen.',
      href: '/ratgeber/us-bankkonto-eroeffnen-auslaender',
      category: 'Banking'
    },
    {
      title: 'Die 5 besten LLC-Gründungsservices 2026',
      excerpt: 'Unser großer Vergleich der führenden Gründungsdienstleister — mit detaillierten Bewertungen und Preisvergleich.',
      href: '/ratgeber/beste-llc-gruendungsservices-vergleich',
      category: 'Vergleich'
    },
    {
      title: 'LLC Steuern für Deutsche erklärt',
      excerpt: 'US-Steuerpflichten für ausländische LLC-Inhaber und was Sie bei der deutschen Steuererklärung beachten müssen.',
      href: '/ratgeber/llc-steuern-deutsche',
      category: 'Steuern'
    },
    {
      title: 'Operating Agreement — Warum es unverzichtbar ist',
      excerpt: 'Was ein Operating Agreement ist, welche Klauseln wichtig sind und warum Sie auch als Einzelunternehmer eines brauchen.',
      href: '/ratgeber/operating-agreement-llc',
      category: 'Rechtliches'
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a2332' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="container-wide relative py-20 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7">
              <div className="overline mb-4">Ihr unabhängiger Ratgeber</div>
              <h1 className="text-navy-900 mb-6">
                Firma im Ausland gründen? <br className="hidden sm:block" />
                <span className="text-teal-600">Wir zeigen Ihnen wie.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-2xl">
                Unabhängige Tests, ehrliche Bewertungen und Schritt-für-Schritt-Anleitungen 
                für Ihre Firmengründung in den USA, UK und Kanada.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/northwest-registered-agent-erfahrungen"
                  className="cta-primary"
                  data-testid="hero-cta-primary"
                >
                  Unseren #1 Testsieger ansehen
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link
                  to="/ratgeber"
                  className="cta-secondary"
                  data-testid="hero-cta-secondary"
                >
                  Alle Ratgeber durchstöbern
                </Link>
              </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-teal-100 to-teal-50 rounded-sm opacity-50" />
                <img
                  src="https://images.unsplash.com/photo-1583338850703-bc602b103674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1vZGVybiUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzUwNTQ2MjJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Moderne Geschäftsarchitektur"
                  className="relative rounded-sm shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-navy-950 py-6" data-testid="trust-bar">
        <div className="container-wide">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm">
            <div className="flex items-center gap-2 text-white">
              <Check className="w-4 h-4 text-teal-400" />
              <span>Über 50 Anbieter getestet</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Shield className="w-4 h-4 text-teal-400" />
              <span>100% unabhängige Bewertungen</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <RefreshCw className="w-4 h-4 text-teal-400" />
              <span>Aktualisiert: April 2026</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Users className="w-4 h-4 text-teal-400" />
              <span>Deutschsprachiger Support-Ratgeber</span>
            </div>
          </div>
        </div>
      </section>

      {/* Top Recommendation */}
      <section className="py-16 lg:py-24 section-light" data-testid="top-recommendation">
        <div className="container-wide">
          <div className="text-center mb-12">
            <div className="overline mb-3">Redaktionsempfehlung</div>
            <h2 className="text-navy-900">Unsere Top-Empfehlung 2026</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="card-highlight p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="badge-teal flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      Testsieger 2026
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-navy-900 mb-4">
                    Northwest Registered Agent
                  </h3>
                  <div className="mb-6">
                    <StarRating rating={4.9} size="lg" />
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Ab $39 + Staatsgebühren</strong> — einer der günstigsten Anbieter</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Kostenloser Registered Agent</strong> für 1 Jahr inklusive</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Datenschutz inklusive</strong> — Ihre Adresse bleibt privat</span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Erstklassiger Kundenservice</strong> mit US-basiertem Support</span>
                    </li>
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://www.awin1.com/cread.php?awinmid=66946&awinaffid=1553052&ued=https%3A%2F%2Fwww.northwestregisteredagent.com%2Fservice-signup"
                      target="_blank"
                      rel="nofollow sponsored noopener noreferrer"
                      className="cta-primary"
                      data-testid="recommendation-cta"
                    >
                      Jetzt LLC gründen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                    <Link
                      to="/northwest-registered-agent-erfahrungen"
                      className="cta-secondary"
                    >
                      Testbericht lesen
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What do you want to form? */}
      <section className="py-16 lg:py-24" data-testid="formation-options">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-navy-900">Was möchten Sie gründen?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* USA LLC */}
            <Link
              to="/llc-usa-gruenden"
              className="group card-flat overflow-hidden"
              data-testid="card-usa"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/33620019/pexels-photo-33620019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="New York Wall Street"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🇺🇸</span>
                  <h3 className="font-heading text-xl font-bold text-navy-900">
                    LLC in den USA gründen
                  </h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Die beliebteste Rechtsform für internationale Unternehmer. Haftungsbeschränkung, 
                  steuerliche Flexibilität und einfache Verwaltung.
                </p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* UK LTD */}
            <Link
              to="/ltd-uk-gruenden"
              className="group card-flat overflow-hidden"
              data-testid="card-uk"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1728341651771-49116b839d18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxsb25kb24lMjBmaW5hbmNpYWwlMjBkaXN0cmljdCUyMHNreWxpbmV8ZW58MHx8fHwxNzc1MDU0NTk5fDA&ixlib=rb-4.1.0&q=85"
                  alt="London Financial District"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🇬🇧</span>
                  <h3 className="font-heading text-xl font-bold text-navy-900">
                    LTD in Großbritannien gründen
                  </h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Schnell, günstig und international anerkannt. Ideal für europäische 
                  Unternehmer und E-Commerce.
                </p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Canada */}
            <Link
              to="/firma-gruenden-kanada"
              className="group card-flat overflow-hidden"
              data-testid="card-canada"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/1248511/pexels-photo-1248511.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Toronto Business Skyline"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🇨🇦</span>
                  <h3 className="font-heading text-xl font-bold text-navy-900">
                    Firma in Kanada gründen
                  </h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">
                  Stabile Wirtschaft, starker Ruf und attraktive Steuervorteile 
                  für internationale Gründer.
                </p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24 section-light" data-testid="how-it-works">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-navy-900 mb-4">So funktioniert es — in 3 Schritten</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Von der ersten Recherche bis zur fertigen Firma: Wir begleiten Sie durch den gesamten Prozess.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-600 text-white font-heading text-2xl font-bold rounded-sm">
                1
              </div>
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">
                Rechtsform wählen
              </h3>
              <p className="text-slate-600">
                Lesen Sie unsere Ratgeber und entscheiden Sie, ob eine LLC, LTD oder 
                kanadische Corporation für Sie am besten passt.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-600 text-white font-heading text-2xl font-bold rounded-sm">
                2
              </div>
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">
                Anbieter auswählen
              </h3>
              <p className="text-slate-600">
                Nutzen Sie unsere Vergleiche und Tests. Wir empfehlen Northwest 
                Registered Agent als unseren Testsieger.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-teal-600 text-white font-heading text-2xl font-bold rounded-sm">
                3
              </div>
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-3">
                Online gründen
              </h3>
              <p className="text-slate-600">
                Schließen Sie die Gründung in nur 10–15 Minuten online ab. 
                Ihr Anbieter kümmert sich um den Rest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Guides */}
      <section className="py-16 lg:py-24" data-testid="latest-guides">
        <div className="container-wide">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-navy-900">Neueste Ratgeber</h2>
            <Link
              to="/ratgeber"
              className="hidden sm:inline-flex items-center gap-1 text-teal-600 font-medium hover:gap-2 transition-all"
            >
              Alle Ratgeber
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide, index) => (
              <Link
                key={index}
                to={guide.href}
                className="group card-flat p-6 hover:shadow-sm"
                data-testid={`guide-card-${index}`}
              >
                <span className="text-xs font-medium text-teal-600 uppercase tracking-wider">
                  {guide.category}
                </span>
                <h3 className="font-heading text-lg font-bold text-navy-900 mt-2 mb-3 group-hover:text-teal-700 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {guide.excerpt}
                </p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                  Jetzt lesen
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="sm:hidden text-center mt-8">
            <Link
              to="/ratgeber"
              className="inline-flex items-center gap-1 text-teal-600 font-medium"
            >
              Alle Ratgeber ansehen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-16 lg:py-24 section-light" data-testid="trust-section">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-navy-900 mb-4">Warum ReviewLex vertrauen?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white border border-slate-200 rounded-sm">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
              <h4 className="font-heading font-bold text-navy-900 mb-2">Unabhängiges Team</h4>
              <p className="text-slate-600 text-sm">
                Wir sind ein unabhängiges Redaktionsteam mit Sitz in Deutschland.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white border border-slate-200 rounded-sm">
                <Star className="w-6 h-6 text-teal-600" />
              </div>
              <h4 className="font-heading font-bold text-navy-900 mb-2">10-Punkte-System</h4>
              <p className="text-slate-600 text-sm">
                Jeder Anbieter wird nach einem standardisierten Bewertungssystem geprüft.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white border border-slate-200 rounded-sm">
                <Shield className="w-6 h-6 text-teal-600" />
              </div>
              <h4 className="font-heading font-bold text-navy-900 mb-2">Transparente Finanzierung</h4>
              <p className="text-slate-600 text-sm">
                Wir verdienen über Affiliate-Links — unsere Bewertungen bleiben davon unberührt.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-white border border-slate-200 rounded-sm">
                <RefreshCw className="w-6 h-6 text-teal-600" />
              </div>
              <h4 className="font-heading font-bold text-navy-900 mb-2">Regelmäßig aktualisiert</h4>
              <p className="text-slate-600 text-sm">
                Unsere Inhalte werden regelmäßig überprüft und aktualisiert.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
